import { Collapsible } from "@base-ui/react/collapsible";
import { useRender } from "@base-ui/react/use-render";
import { ChevronRight } from "lucide-react";
import { type ComponentProps, createContext, type ReactElement, type ReactNode, use, useId } from "react";
import { cn } from "../cn";
import { Tooltip } from "../components/tooltip";

// The navigation of an app (design-system.md §4.13, ADR-114): places in sections that close one by one, the "your
// turn" count on every place and on a closed section, and the narrow form, a column of icons with their names in a
// tooltip. It knows nothing of routes and permissions: the app passes the link of every place and whether it is the
// one on display.

const RailContext = createContext(false);

/** Whether the sidebar is the narrow column of icons: the app draws its own header and footer accordingly. */
export const useSidebarRail = () => use(RailContext);

interface SidebarProps extends ComponentProps<"nav"> {
  /** Accessible name of the navigation landmark. */
  label: string;
  /** The column of icons instead of names (only where the sidebar is a column, never in the sheet). */
  rail?: boolean;
}

export function Sidebar({ label, rail = false, className, children, ...props }: SidebarProps) {
  return (
    <RailContext value={rail}>
      <nav
        aria-label={label}
        data-rail={rail || undefined}
        className={cn("flex min-h-0 flex-col", className)}
        {...props}
      >
        {children}
      </nav>
    </RailContext>
  );
}

/**
 * The list of places that scrolls inside the sidebar. Its items have View Transition names (the indicator and their
 * content), and a named element is drawn above the page unclipped: the list is the group that holds and clips them, or
 * the items hidden past its edge flash at the bottom of the sidebar during every navigation (styles/index.css).
 */
export function SidebarPlaces({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-sidebar-places=""
      className={cn("flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto", className)}
      {...props}
    />
  );
}

interface YourTurnBadgeProps {
  count: number;
  /** For screen readers, after the number: "waiting on you". */
  label: string;
  /** Only the dot: the narrow sidebar, where the number does not fit. */
  dotOnly?: boolean;
  /** Beyond it the badge says "99+": a count that stops there (the unread of the bell, ADR-280). */
  max?: number;
  className?: string;
}

/** How many items wait on the user (design-system.md §5.1): the brand dot and the number. */
export function YourTurnBadge({ count, label, dotOnly = false, max, className }: YourTurnBadgeProps) {
  if (count <= 0) return null;
  return (
    <span className={cn("flex items-center gap-1 text-xs tabular-nums", className)}>
      <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-brand" />
      <span className={dotOnly ? "sr-only" : undefined}>
        {max !== undefined && count > max ? `${max}+` : count}
        <span className="sr-only"> {label}</span>
      </span>
    </span>
  );
}

interface SidebarSectionProps {
  label: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Shown only while the section is closed: the sum of its places. */
  badge?: ReactNode;
  children: ReactNode;
}

/** A section of places: a heading that opens and closes it; in the narrow sidebar a group of icons under a rule. */
export function SidebarSection({ label, open, onOpenChange, badge, children }: SidebarSectionProps) {
  const rail = useSidebarRail();
  if (rail) {
    return (
      // biome-ignore lint/a11y/useSemanticElements: a group of links named by the section, not a form fieldset
      <div role="group" aria-label={label} className="mt-2 flex shrink-0 flex-col gap-0.5 border-t border-border pt-2">
        {children}
      </div>
    );
  }
  return (
    <Collapsible.Root open={open} onOpenChange={(next) => onOpenChange(next)} className="mt-3 flex shrink-0 flex-col">
      <Collapsible.Trigger className="group/section flex h-7 pointer-coarse:h-(--control-h) items-center gap-1.5 rounded-md px-2 text-[0.6875rem] font-medium tracking-wide text-muted-foreground uppercase hover:text-foreground">
        <span className="flex-1 truncate text-left">{label}</span>
        {!open && badge}
        <ChevronRight
          aria-hidden
          className="size-3.5 shrink-0 transition-transform duration-150 group-data-[panel-open]/section:rotate-90"
        />
      </Collapsible.Trigger>
      <Collapsible.Panel className="flex flex-col gap-0.5 pt-0.5">{children}</Collapsible.Panel>
    </Collapsible.Root>
  );
}

interface SidebarItemProps {
  /** The link of the place, e.g. `<Link to="/linesheets" />`: the item gives it classes and content. */
  render: ReactElement;
  icon: ReactNode;
  label: string;
  /**
   * The place on display: the item shows the indicator, which slides from place to place with View Transitions. One
   * item at most, or the transition is skipped: a pinned copy of a place is never active.
   */
  active?: boolean;
  /** "Your turn" count; in the narrow sidebar only its dot. */
  count?: number;
  countLabel?: string;
  /** The count beyond which the badge says "99+" (`YourTurnBadge`). */
  countMax?: number;
  /** Keys of the shortcut, shown in the tooltip of the narrow sidebar. */
  shortcut?: ReactNode;
  /** A button next to the link, shown on hover (e.g. unpin); never in the narrow sidebar. */
  action?: ReactNode;
}

export function SidebarItem(props: SidebarItemProps) {
  const { render, icon, label, active = false, count = 0, countLabel = "", countMax, shortcut, action } = props;
  const rail = useSidebarRail();
  // every View Transition draws the named indicator above the page, and so above the item's own text: the content
  // has a name of its own, painted after the indicator, so it stays on top while the indicator slides under it
  const contentName = `sidebar-item-${useId().replace(/[^\w-]/g, "")}`;
  const link = useRender({
    render,
    props: {
      className: cn(
        "relative isolate flex h-(--control-h) shrink-0 rounded-md text-sm hover:bg-accent [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground",
        rail && "w-(--control-h)",
        active && "font-medium [&_svg]:text-foreground",
      ),
      children: (
        <>
          {active && (
            <span
              aria-hidden
              className="absolute inset-0 -z-10 rounded-md border border-border bg-background shadow-[inset_2px_0_0_var(--brand)] [view-transition-name:sidebar-active]"
            />
          )}
          <span
            style={{ viewTransitionName: contentName }}
            className={cn("relative flex min-w-0 flex-1 items-center gap-2", rail ? "justify-center" : "px-2")}
          >
            {icon}
            <span className={rail ? "sr-only" : "min-w-0 flex-1 truncate"}>{label}</span>
            <YourTurnBadge
              count={count}
              label={countLabel}
              dotOnly={rail}
              {...(countMax !== undefined && { max: countMax })}
              className={cn(
                rail && "absolute top-1 right-1",
                action && "group-hover/item:invisible pointer-coarse:invisible",
              )}
            />
          </span>
        </>
      ),
    },
  });
  if (rail) {
    return (
      <Tooltip
        content={
          <>
            {label}
            {shortcut}
          </>
        }
      >
        {link}
      </Tooltip>
    );
  }
  if (!action) return link;
  return (
    <div className="group/item relative shrink-0">
      {link}
      <div className="absolute inset-y-0 right-1 flex items-center opacity-0 group-hover/item:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100">
        {action}
      </div>
    </div>
  );
}
